import { Suspense, useLayoutEffect, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, X, BookOpen } from 'lucide-react';
import { NaphthaleneScene3D } from './lab/NaphthaleneScene3D';
import { NaphthaleneSidebar } from './panels/NaphthaleneSidebar';
import { CameraController } from './lab/CameraController';
import NaphthaleneToolbar from './lab/NaphthaleneToolbar';
import LoaderOverlay from './lab/LoaderOverlay';
import { useNaphthaleneStore } from './store/useNaphthaleneStore';
import { ThemeManager } from '../titration/lib/ThemeManager';
import { useCurrentUser } from '../../AuthComponents';
import {
  useExperiment,
  useStudentProgress,
  usePreLabQuestions,
  useSubmitPreLabAssessment,
  useTeacherBypass,
  useSaveLabData,
} from '../../../utils/convexHooks';
import { Id } from '../../../convex/_generated/dataModel';
import PreLabAssessment from '../../PreLabAssessment';

function WebGLStateTracker({ onReady }: { onReady: () => void }) {
  const frameCount = useRef(0);
  useFrame(() => {
    frameCount.current += 1;
    if (frameCount.current === 2) onReady();
  });
  return null;
}

function TutorialOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative max-w-xl w-[92vw] bg-card text-foreground rounded-2xl shadow-2xl border border-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted/70 text-muted-foreground hover:text-foreground transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
              How this experiment works
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Melting and freezing point of naphthalene</p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-1">Aim</p>
            <p className="text-foreground">Determine the melting point and freezing point of naphthalene (C<sub>10</sub>H<sub>8</sub>) using a water bath.</p>
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mt-4">Procedure</p>
          <ol className="list-decimal list-inside space-y-1.5 ml-2 text-foreground">
            <li>Add naphthalene to the boiling tube (≈ ⅓ filled).</li>
            <li>Insert the thermometer into the boiling tube.</li>
            <li>Pour water into the beaker (water bath).</li>
            <li>Immerse the boiling tube into the water bath.</li>
            <li>Heat the water bath with the Bunsen burner.</li>
            <li>Stir continuously and record the temperature every 30 s.</li>
            <li>When fully liquid (~95 °C), transfer to the conical flask.</li>
            <li>Stir while cooling — observe the freezing plateau.</li>
          </ol>

          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Melting</p>
              <p className="text-lg font-bold text-orange-500">~80 °C</p>
              <p className="text-[11px] text-muted-foreground">plateau</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Freezing</p>
              <p className="text-lg font-bold text-blue-500">~80 °C</p>
              <p className="text-[11px] text-muted-foreground">plateau</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Watch</p>
              <p className="text-lg font-bold text-purple-500">supercooling</p>
              <p className="text-[11px] text-muted-foreground">if not stirred</p>
            </div>
          </div>

          <div className="pt-2 border-t border-border text-xs">
            <p>
              <strong className="text-foreground">Tip:</strong> The fast-forward button (1× → 2× → 5× → 10×) accelerates heating &amp; cooling. <strong className="text-foreground">Auto-Demo</strong> runs the entire experiment for you.
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium transition shadow-md shadow-orange-500/20"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

interface BaseProps {
  experimentConfig: any;
}

function NaphthaleneExperimentBase({ experimentConfig }: BaseProps) {
  const reset = useNaphthaleneStore(s => s.reset);
  const recordedData = useNaphthaleneStore(s => s.recordedData);
  const labStage = useNaphthaleneStore(s => s.labStage);
  const showTutorial = useNaphthaleneStore(s => s.showTutorial);
  const setShowTutorial = useNaphthaleneStore(s => s.setShowTutorial);
  const [webGLReady, setWebGLReady] = useState(false);
  const controlsRef = useRef<any>(null);

  const { user } = useCurrentUser();
  const saveLabData = useSaveLabData();

  useLayoutEffect(() => {
    ThemeManager.getInstance().setMode(ThemeManager.getInstance().getMode());
  }, []);

  useEffect(() => {
    reset();
  }, [reset]);

  const savedRef = useRef(false);
  useEffect(() => {
    if (labStage === 'completed' && !savedRef.current && user && experimentConfig?._id) {
      savedRef.current = true;
      saveLabData({
        studentId: user._id,
        experimentConfigId: experimentConfig._id,
        dataPoints: recordedData,
      }).catch((e: any) => console.error('saveLabData failed:', e));
    }
  }, [labStage, recordedData, user, experimentConfig, saveLabData]);

  const title = experimentConfig?.title || 'Melting Point of Naphthalene';
  const experimentMode = experimentConfig?.experimentMode || 'practice';

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-background relative text-foreground">
      <NaphthaleneToolbar title={title} experimentMode={experimentMode} />

      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative w-full h-full bg-background">
        <div className="flex-[2] lg:flex-[3] relative w-full h-full min-h-[50vh] lg:min-h-0 bg-background/50">
          <div className="absolute inset-0">
            <LoaderOverlay isWebGLReady={webGLReady} />
            <Canvas shadows dpr={[1, 2]} gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }} className="w-full h-full">
              <WebGLStateTracker onReady={() => setWebGLReady(true)} />
              <PerspectiveCamera makeDefault position={[0, 1.5, 4]} fov={45} />
              <OrbitControls ref={controlsRef} enablePan panSpeed={0.5} minDistance={0.5} maxDistance={15} target={[0, 1, 0]} />
              <CameraController controlsRef={controlsRef} />
              <ambientLight intensity={0.55} />
              <directionalLight position={[5, 8, 5]} intensity={0.85} />
              <directionalLight position={[-3, 4, -2]} intensity={0.3} color="#8ab4ff" />
              <Suspense fallback={null}>
                <NaphthaleneScene3D />
              </Suspense>
            </Canvas>
          </div>
        </div>
        <NaphthaleneSidebar />
      </div>

      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </div>
  );
}

export default function NaphthaleneExperimentWrapper() {
  const [searchParams] = useSearchParams();
  const idStr = searchParams.get('id');
  const id = idStr ? (idStr as Id<'experimentConfigs'>) : undefined;

  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const experimentConfig = useExperiment(id);
  const progress = useStudentProgress(user?._id, id);
  const preLabQuestions = usePreLabQuestions(id);
  const submitPreLab = useSubmitPreLabAssessment();
  const teacherBypass = useTeacherBypass();

  if (!id) {
    return <NaphthaleneExperimentBase experimentConfig={null} />;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl">Please sign in to start the experiment.</p>
        <button onClick={() => navigate('/sign-in')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Sign in</button>
      </div>
    );
  }

  if (experimentConfig === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl text-red-500">Experiment not found</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back to Dashboard</button>
      </div>
    );
  }

  if (
    experimentConfig === undefined ||
    progress === undefined ||
    (experimentConfig.preLab.enabled && preLabQuestions === undefined)
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500 mb-4" />
        <p className="text-sm text-muted-foreground tracking-wider uppercase">Preparing the lab…</p>
      </div>
    );
  }

  const needsPreLab =
    experimentConfig.preLab.enabled &&
    progress !== null &&
    (progress.status === 'not-started' || progress.status === 'pre-lab') &&
    (!progress.preLab || !progress.preLab.passed);

  if (needsPreLab && preLabQuestions && preLabQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-background py-8 px-4 text-foreground">
        <button
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 flex flex-row gap-2 items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </button>
        <PreLabAssessment
          questions={preLabQuestions.map((q: any) => ({ ...q, id: q._id as string }))}
          passingScore={experimentConfig.preLab.passingScore}
          maxAttempts={experimentConfig.preLab.maxAttempts}
          showAnswersAfterFail={experimentConfig.preLab.showAnswersAfterFail}
          allowTeacherBypass={experimentConfig.allowTeacherBypass}
          onPass={async (answers) => {
            try {
              await submitPreLab({
                experimentConfigId: id,
                studentId: user._id,
                answers: answers.map((a: any) => ({
                  questionId: a.questionId as Id<'preLabQuestions'>,
                  answer: a.answer,
                })),
              });
            } catch (e: any) {
              alert(e.message || 'Failed to save pre-lab progress.');
            }
          }}
          onBypass={async () => {
            try {
              await teacherBypass({ experimentConfigId: id, studentId: user._id });
            } catch (e: any) {
              alert(e.message || 'Invalid password');
            }
          }}
        />
      </div>
    );
  }

  return <NaphthaleneExperimentBase experimentConfig={experimentConfig} />;
}
